import React, { useEffect } from 'react';

const SeoContent: React.FC = () => {
  // Inject FAQPage JSON-LD on mount
  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How does Terraform variable precedence work?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Terraform loads variables in a specific order. The precedence, from lowest to highest, is: 1. Environment variables (TF_VAR_name), 2. terraform.tfvars files, 3. *.auto.tfvars files, and 4. -var or -var-file command-line options.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the difference between Terragrunt inputs and Terraform overrides?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Terraform uses variable blocks and .tfvars files to assign values. Terragrunt abstracts this by using an inputs block in a terragrunt.hcl file. Terragrunt then passes these inputs to Terraform as environment variables or a generated vars file.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does this work with OpenTofu?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. OpenTofu is a fork of Terraform and maintains compatibility with the HCL syntax and the provider ecosystem. The HCL generated by this tool is 100% compatible with OpenTofu versions 1.6+.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is my code sent to a server?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. All parsing, generation, formatting, and validation happens 100% in your browser. The only network call is fetching variables.tf from public GitHub repositories (user-initiated). No infrastructure code, variable values, or configuration data is ever sent to our servers or any third-party API.',
          },
        },
        {
          '@type': 'Question',
          name: 'Why use a wrapper module pattern?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The wrapper module pattern involves creating a root module that instantiates a child module (e.g., from the public registry) and mapping root variables to module variables. This allows you to simplify the interface for end-users of your infrastructure or set organization-wide defaults.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I use generated provider configurations?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The tool generates a provider.tf file containing the required_providers block and provider configuration. Place this in the root of your project directory before running terraform init.',
          },
        },
      ],
    };

    const existingFaq = document.getElementById('json-ld-faq');
    if (!existingFaq) {
      const script = document.createElement('script');
      script.id = 'json-ld-faq';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(faqSchema);
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById('json-ld-faq');
      if (el) el.remove();
    };
  }, []);

  return (
    <article className="max-w-4xl mx-auto px-6 py-16 text-slate-700 dark:text-slate-300">
      {/* Homepage Content: Introduction & Problem Solving */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">
          Generate Terraform Override Files — Privately, in Your Browser
        </h2>
        <div className="prose dark:prose-invert max-w-none space-y-4">
          <p>
            Managing Infrastructure as Code (IaC) often involves wrangling complex variable
            definitions. Whether you are using
            <strong>Terraform</strong>, <strong>Terragrunt</strong>, or <strong>OpenTofu</strong>,
            defining overrides manually is error-prone and tedious. The{' '}
            <strong>TF Override Generator</strong> is a specialized tool designed for DevOps
            engineers and SREs to automate the creation of configuration files.
          </p>
          <p>
            Unlike other tools, TF Override Generator processes everything{' '}
            <strong>locally in your browser</strong>. Your infrastructure code, variable values, and
            configuration data never leave your device. The only network request is fetching{' '}
            <code>variables.tf</code> from public GitHub repositories — and only when you explicitly
            click "Analyze."
          </p>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mt-8 mb-4">
            How It Works — No AI Required
          </h3>
          <p>
            TF Override Generator uses a deterministic, template-based approach to generate HCL
            files. Given your selected provider, region, module URL, and variable overrides, the
            tool produces identical, correct output every time — instantly, without waiting for an
            API response. The built-in HCL parser handles complex variable types including nested
            objects, lists, maps, heredocs, and optional/nullable fields.
          </p>
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mt-8 mb-4">
            Understanding Override Files (<code>_override.tf</code>)
          </h3>
          <p>
            In Terraform, files ending in <code>_override.tf</code> or{' '}
            <code>_override.tf.json</code> are loaded last. This allows you to override parts of
            your configuration without modifying the main configuration files, which is critical
            when working with generated code or temporary environment patches.
          </p>
          <p>
            This tool generates standard <code>overrides.tf</code> files using the "Wrapper Module"
            pattern, creating a clean separation between your module instantiation and the variable
            inputs.
          </p>
        </div>
      </section>

      {/* Technical FAQs */}
      <section>
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8">
          Frequently Asked Questions
        </h2>
        <div className="grid gap-8 md:grid-cols-2">
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              How does Terraform variable precedence work?
            </h3>
            <p className="text-sm leading-relaxed">
              Terraform loads variables in a specific order. The precedence, from lowest to highest,
              is: 1. Environment variables (`TF_VAR_name`), 2. `terraform.tfvars` files, 3.
              `*.auto.tfvars` files, and 4. `-var` or `-var-file` command-line options. This tool
              generates files that can be used as a source of truth for these variables.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              What is the difference between Terragrunt inputs and Terraform overrides?
            </h3>
            <p className="text-sm leading-relaxed">
              Terraform uses `variable` blocks and `.tfvars` files to assign values. Terragrunt
              abstracts this by using an `inputs = {'{ ... }'}` block in a `terragrunt.hcl` file.
              Terragrunt then passes these inputs to Terraform as environment variables or a
              generated vars file. Our tool supports generating both formats.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              Does this work with OpenTofu?
            </h3>
            <p className="text-sm leading-relaxed">
              Yes. OpenTofu is a fork of Terraform and maintains compatibility with the HCL syntax
              and the provider ecosystem. The HCL generated by this tool is 100% compatible with
              OpenTofu versions 1.6+.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              Why use a wrapper module pattern?
            </h3>
            <p className="text-sm leading-relaxed">
              The wrapper module pattern involves creating a root module that instantiates a child
              module (e.g., from the public registry) and mapping root variables to module
              variables. This allows you to simplify the interface for end-users of your
              infrastructure or set organization-wide defaults.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              Is my code sent to a server?
            </h3>
            <p className="text-sm leading-relaxed">
              No. All parsing, generation, formatting, and validation happens 100% in your browser.
              The only network call is fetching <code>variables.tf</code> from public GitHub
              repositories — and only when you explicitly click "Analyze." No infrastructure code,
              variable values, or configuration data is ever sent to our servers or any third-party
              API.
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              How do I use generated provider configurations?
            </h3>
            <p className="text-sm leading-relaxed">
              The tool generates a `provider.tf` file containing the `required_providers` block and
              provider configuration. You should place this in the root of your project directory
              before running `terraform init`.
            </p>
          </div>
        </div>
      </section>
    </article>
  );
};

export default SeoContent;
